import React, { useEffect, useRef, useState } from "react";

import CloseIcon from "@mui/icons-material/Close";
import { IconButton, Stack, TextField, Typography } from "@mui/material";
import Box from "@mui/material/Box";
import Modal from "@mui/material/Modal";
import { toast } from "react-toastify";

import { Button } from "components/forms";
import { useCreateClientMutation } from "queries/query-selectors/clients-query-selectors";
import { CreateClientPayload } from "store/types/user-management";

import "./AddClientModal.styles.scss";

interface AddClientModalProps {
  open: boolean;
  onClose: () => void;
  onSuccess: (clientId: number) => void;
  onNavigateToClient?: (clientId: number) => void;
}

const style = {
  position: "absolute" as "absolute",
  top: "50%",
  left: "50%",
  transform: "translate(-50%, -50%)"
};

/**
 * Dialog for creating a new client.
 * Handles form state, submit loading, and error display.
 * On success, fires onClose to dismiss and onSuccess with the new client ID.
 */
export const AddClientModal: React.FC<AddClientModalProps> = ({ open, onClose, onSuccess, onNavigateToClient }) => {
  const [name, setName] = useState("");
  const [type, setType] = useState("");
  const [rm, setRm] = useState("");
  const [country, setCountry] = useState("");
  const [currency, setCurrency] = useState("");
  const [errorMessage, setErrorMessage] = useState("");
  const nameRef = useRef<HTMLInputElement>(null);

  // Auto-focus the Name field when modal opens.
  useEffect(() => {
    if (open) {
      nameRef.current?.focus();
    }
  }, [open]);

  // Real mutation hooked to the Laravel create client endpoint.
  const { mutateAsync, isPending: isMutationPending } = useCreateClientMutation({
    onMutationSuccess: () => {
      // refetch is handled automatically by the mutation's onSettled
    },
  });

  const handleSubmit = async () => {
    if (!name.trim()) {
      setErrorMessage("Client name is required.");
      return;
    }

    setErrorMessage("");

    const payload: CreateClientPayload = {
      name: name.trim(),
      type: type.trim() || null,
      relationship_manager: rm.trim() || null,
      country: country.trim() || null,
      primary_currency: currency.trim() || null,
    };

    try {
      const newClient = await mutateAsync(payload);

      // Success lifecycle: close modal, notify parent, show toast, navigate to new client page.
      toast.success("Client created successfully");
      onClose();
      onSuccess(newClient);
      onNavigateToClient?.(newClient);
    } catch (error: unknown) {
      // Handle 422 validation errors — specifically duplicate name.
      const err = error as { response?: { status?: number }; data?: { errors?: Record<string, string[]> } };
      const isDuplicateName =
        err?.response?.status === 422 &&
        err?.data?.errors?.name?.some((msg: string) =>
          msg.toLowerCase().includes("already exists") || msg.toLowerCase().includes("duplicate"),
        );

      if (isDuplicateName) {
        setErrorMessage("A client with this name already exists.");
      } else {
        setErrorMessage("Something went wrong. Please try again.");
      }
    }
  };

  const isFormValid = name.trim().length > 0;

  return (
    <Modal open={open} onClose={onClose} disableAutoFocus>
      <Box sx={style}>
        <Stack
          className="add-client-modal"
          sx={{
            width: 500,
            backgroundColor: "white",
            borderRadius: "10px"
          }}
          direction="column"
        >
          <Box sx={{ padding: "24px 24px 0 24px" }}>
            <Stack
              direction="row"
              justifyContent="space-between"
              alignItems="center"
              sx={{ borderBottom: "1px solid #dae0e2", paddingBottom: "5px" }}
            >
              <Typography variant="h6" sx={{ color: "#0a3142", fontWeight: "500" }}>
                Add client
              </Typography>
              <IconButton onClick={onClose} size="small">
                <CloseIcon className="icon" />
              </IconButton>
            </Stack>
          </Box>

          <Stack sx={{ padding: "24px", gap: "16px" }}>
            <TextField
              label="Client name"
              fullWidth
              value={name}
              onChange={(e) => setName(e.target.value)}
              disabled={isMutationPending}
              inputProps={{ "aria-label": "client name" }}
              inputRef={nameRef}
              autoFocus
            />

            <TextField
              label="Type"
              fullWidth
              value={type}
              onChange={(e) => setType(e.target.value)}
              disabled={isMutationPending}
              placeholder="e.g. Individual, Family, Legal entity"
              inputProps={{ "aria-label": "client type" }}
            />

            <TextField
              label="RM"
              fullWidth
              value={rm}
              onChange={(e) => setRm(e.target.value)}
              disabled={isMutationPending}
              placeholder="Relationship manager"
              inputProps={{ "aria-label": "relationship manager" }}
            />

            <TextField
              label="Country"
              fullWidth
              value={country}
              onChange={(e) => setCountry(e.target.value)}
              disabled={isMutationPending}
              inputProps={{ "aria-label": "country" }}
            />

            <TextField
              label="Currency"
              fullWidth
              value={currency}
              onChange={(e) => setCurrency(e.target.value)}
              disabled={isMutationPending}
              inputProps={{ "aria-label": "currency" }}
            />

            <Typography variant="caption" sx={{ color: "#898989", marginTop: "4px" }}>
              Entities and fees will be managed later.
            </Typography>

            {errorMessage && (
              <Typography className="add-client-modal__error" variant="body2">
                {errorMessage}
              </Typography>
            )}
          </Stack>

          <Stack
            className="add-client-modal__actions"
            direction="row"
            justifyContent="flex-end"
            sx={{ padding: "0 24px 24px 24px", gap: "12px" }}
          >
            <Button title="Cancel" variant="light" disabled={isMutationPending} onClick={onClose} />
            <Button
              title={isMutationPending ? "Creating..." : "Create client"}
              variant="primary"
              disabled={!isFormValid || isMutationPending}
              onClick={handleSubmit}
              isLoading={isMutationPending}
            />
          </Stack>
        </Stack>
      </Box>
    </Modal>
  );
};
